import React from "react";
import { motion } from "framer-motion";
import { cn } from "../lib/utils";
import { useNavigationItems } from "../hooks/useNavigationItems";
import {
  Home,
  Blocks,
  MonitorPlay,
  Orbit,
  LibraryBig,
  LayoutList,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface TabDef {
  id: string;
  label: string;
  icon: LucideIcon;
  onClick: () => void;
}

const MobileBottomNav: React.FC = () => {
  const {
    isLoggedIn,
    activeItem,
    navigateToPortalTab,
    navigate,
    t,
  } = useNavigationItems();

  const tabs: TabDef[] = isLoggedIn
    ? [
        {
          id: "home",
          label: t("home"),
          icon: Home,
          onClick: () => navigate("/home"),
        },
        {
          id: "portal_my_workspace",
          label: t("workspace"),
          icon: Blocks,
          onClick: () => {
            localStorage.setItem("workspace_active_tab", "hub");
            navigateToPortalTab("my_workspace");
          },
        },
        {
          id: "portal_activities",
          label: t("run", "Run"),
          icon: MonitorPlay,
          onClick: () => navigateToPortalTab("activities"),
        },
        {
          id: "programs",
          label: t("programs", "Programs"),
          icon: LayoutList,
          onClick: () => navigate("/programs"),
        },
        {
          id: "spaces",
          label: t("spaces"),
          icon: Orbit,
          onClick: () => navigate("/spaces"),
        },
      ]
    : [
        {
          id: "home",
          label: t("home"),
          icon: Home,
          onClick: () => navigate("/home"),
        },
        {
          id: "marketplace",
          label: t("marketplace"),
          icon: LibraryBig,
          onClick: () => navigate("/marketplace"),
        },
      ];

  // Map activeItem to the correct tab id
  const getIsActive = (tabId: string) => {
    if (tabId === activeItem) return true;
    // "marketplace" activeItem matches the marketplace tab
    if (tabId === "marketplace" && activeItem === "marketplace") return true;
    return false;
  };

  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-gray-200/60"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div className="flex items-center justify-around h-14">
        {tabs.map((tab) => {
          const isActive = getIsActive(tab.id);
          return (
            <MobileTabButton
              key={tab.id}
              id={tab.id}
              icon={tab.icon}
              label={tab.label}
              isActive={isActive}
              onClick={tab.onClick}
            />
          );
        })}
      </div>
    </nav>
  );
};

// ─── Tab Button ──────────────────────────────────────────────────────────────

interface MobileTabButtonProps {
  id: string;
  icon: LucideIcon;
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const MobileTabButton: React.FC<MobileTabButtonProps> = ({
  id,
  icon: Icon,
  label,
  isActive,
  onClick,
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        "relative flex flex-col items-center justify-center min-w-[48px] min-h-[48px] flex-1 transition-colors duration-200",
        isActive
          ? "text-primary"
          : "text-muted-foreground/60",
      )}
    >
      {/* Animated top indicator */}
      {isActive && (
        <motion.div
          layoutId="mobile-nav-indicator"
          className="absolute top-0 inset-x-3 h-[2px] rounded-full bg-primary"
          transition={{ type: "spring", stiffness: 500, damping: 35 }}
        />
      )}
      <Icon className="w-5 h-5" />
      <span className="text-[11px] font-medium mt-0.5 leading-tight">
        {label}
      </span>
    </button>
  );
};

export default MobileBottomNav;
